'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Building2,
  CalendarCheck,
  Car,
  Check,
  ChevronDown,
  FileSignature,
  Gauge,
  Globe,
  Mail,
  Users,
  Wallet,
} from 'lucide-react';

import { useLocale } from '@/lib/i18n';
import { SUBSCRIPTION_TIERS, TIER_MONTHLY_MAD, type SubscriptionTier } from '@/lib/platform/tiers';
import { DemoRequestForm } from './DemoRequestForm';
import { DeskPreview } from './DeskPreview';

const FEATURES = [
  { key: 'fleet', icon: Car },
  { key: 'bookings', icon: CalendarCheck },
  { key: 'clients', icon: Users },
  { key: 'contracts', icon: FileSignature },
  { key: 'cash', icon: Wallet },
  { key: 'analytics', icon: BarChart3 },
  { key: 'site', icon: Globe },
  { key: 'multi', icon: Building2 },
] as const;

function Reveal({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      node.classList.add('is-in');
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      node.classList.add('is-in');
      observer.disconnect();
    }, { threshold: 0.12 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} className={`wp-reveal ${className}`}>{children}</div>;
}

const STEPS = ['1', '2', '3', '4'] as const;

const PHOTO_CREDITS = [
  { href: 'https://www.pexels.com/photo/white-suv-car-on-the-road-14776590/', label: 'Ambady Kolazhikaran' },
  { href: 'https://www.pexels.com/photo/row-of-new-cars-in-outdoor-parking-lot-29566899/', label: 'Luke Miller' },
  { href: 'https://www.pexels.com/photo/person-in-grey-shirt-handing-keys-97079/', label: 'Negative Space' },
  { href: 'https://www.pexels.com/photo/a-person-polishing-the-white-car-5233279/', label: 'Khunkorn Laowisit' },
  { href: 'https://www.pexels.com/photo/three-business-people-looking-at-laptop-on-the-table-8133855/', label: 'Alena Darmel' },
] as const;

const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#9a3412]';

function planName(tier: SubscriptionTier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function MarketingPhoto({
  name,
  alt,
  width,
  height,
  priority = false,
  className,
}: {
  name: string;
  alt: string;
  width: number;
  height: number;
  priority?: boolean;
  className?: string;
}) {
  return (
    <img
      src={`/marketing/${name}.webp`}
      srcSet={`/marketing/${name}-800.webp 800w, /marketing/${name}.webp ${width}w`}
      sizes="(min-width: 1024px) 640px, 100vw"
      alt={alt}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding="async"
      className={className}
    />
  );
}

function Brand({ light = false }: { light?: boolean }) {
  return (
    <span className="inline-flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF8A00] text-stone-950">
        <Gauge size={16} />
      </span>
      <span className={`text-[15px] font-semibold tracking-tight ${light ? 'text-white' : 'text-stone-950'}`}>
        fleetava
      </span>
    </span>
  );
}

function Hero() {
  const { t } = useLocale();
  const rows = [
    { time: t('landing.preview.row1t'), title: t('landing.preview.row1'), status: t('landing.preview.row1s'), tone: 'ready' as const },
    { time: t('landing.preview.row2t'), title: t('landing.preview.row2'), status: t('landing.preview.row2s'), tone: 'active' as const },
    { time: t('landing.preview.row3t'), title: t('landing.preview.row3'), status: t('landing.preview.row3s'), tone: 'soon' as const },
  ];

  return (
    <section id="top" className="relative mx-auto grid max-w-6xl items-center gap-12 overflow-hidden px-5 py-14 sm:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:py-20">
      <span aria-hidden className="pointer-events-none absolute -start-24 top-8 h-56 w-56 rounded-full bg-[#FF8A00]/15 blur-3xl" />
      <div className="marketing-rise relative">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9a3412]">
          {t('landing.hero.badge')}
        </p>
        <h1 className="mt-4 max-w-xl text-4xl font-semibold leading-[1.08] tracking-tight text-stone-950 sm:text-5xl">
          {t('landing.hero.title')}
        </h1>
        <p className="mt-5 max-w-lg text-base leading-relaxed text-stone-600">{t('landing.hero.subtitle')}</p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a href="#demo" className={`inline-flex items-center gap-2 rounded-full bg-[#FF8A00] px-6 py-3 text-sm font-semibold text-stone-950 transition hover:bg-[#ea7900] ${focusRing}`}>
            {t('landing.hero.ctaPrimary')}
            <ArrowRight size={16} className="rtl:rotate-180" />
          </a>
          <a href="#features" className={`inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-6 py-3 text-sm font-semibold text-stone-800 transition hover:border-stone-400 ${focusRing}`}>
            {t('landing.hero.ctaSecondary')}
          </a>
        </div>
        <p className="mt-5 text-xs font-medium text-stone-500">{t('landing.hero.note')}</p>
      </div>
      <div className="relative grid gap-4">
        <div className="wp-photo overflow-hidden rounded-2xl shadow-xl shadow-stone-900/10 ring-1 ring-stone-900/5">
          <MarketingPhoto
            name="hero-suv"
            alt={t('landing.photo.hero')}
            width={1600}
            height={1200}
            priority
            className="aspect-[16/10] w-full object-cover"
          />
        </div>
        <DeskPreview
          caption={t('landing.preview.caption')}
          labels={{
            fleet: t('landing.preview.fleet'),
            active: t('landing.preview.active'),
            ready: t('landing.preview.ready'),
            returns: t('landing.preview.returns'),
          }}
          rows={rows}
        />
      </div>
    </section>
  );
}

function ProofStrip() {
  const { t } = useLocale();
  const items = [
    t('landing.stats.modules'),
    t('landing.stats.langs'),
    t('landing.stats.tenancy'),
    t('landing.stats.signing'),
  ];
  const loop = [...FEATURES, ...FEATURES];
  return (
    <>
      <section className="border-y border-stone-200 bg-white">
        <ul className="mx-auto grid max-w-6xl grid-cols-2 sm:grid-cols-4">
          {items.map((item) => (
            <li key={item} className="border-stone-200 px-5 py-6 text-sm font-semibold text-stone-800 sm:border-s sm:px-8 sm:first:border-s-0">
              <span className="mb-2 block h-1 w-8 rounded-full bg-[#FF8A00]" />
              {item}
            </li>
          ))}
        </ul>
      </section>
      <div aria-hidden className="overflow-hidden border-b border-stone-200 bg-[#161411] py-3 text-stone-200">
        <div className="wp-marquee-track flex w-max gap-8">
          {loop.map((feature, index) => (
            <span key={`${feature.key}-${index}`} className="text-xs font-semibold tracking-wide">
              {t(`landing.features.${feature.key}.t`)}
              <span className="ms-8 text-[#FF8A00]">●</span>
            </span>
          ))}
        </div>
      </div>
    </>
  );
}

function SplitStory({
  id,
  eyebrow,
  title,
  body,
  image,
  imageAlt,
  imageWidth,
  imageHeight,
}: {
  id?: string;
  eyebrow: string;
  title: string;
  body: string;
  image: string;
  imageAlt: string;
  imageWidth: number;
  imageHeight: number;
}) {
  return (
    <section id={id} className="scroll-mt-32">
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:py-24">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9a3412]">
            {eyebrow}
          </p>
          <h2 className="mt-3 max-w-md text-3xl font-semibold tracking-tight text-stone-950">
            {title}
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-stone-600">{body}</p>
        </div>
        <div className="wp-photo overflow-hidden rounded-2xl shadow-lg shadow-stone-900/10">
          <MarketingPhoto
            name={image}
            alt={imageAlt}
            width={imageWidth}
            height={imageHeight}
            className="aspect-[16/10] w-full object-cover"
          />
        </div>
      </div>
    </section>
  );
}

function ProductStories() {
  const { t } = useLocale();
  return (
    <>
      <SplitStory
        id="features"
        eyebrow={t('landing.features.fleet.t')}
        title={t('landing.show.fleet.title')}
        body={t('landing.show.fleet.body')}
        image="fleet-row"
        imageAlt={t('landing.photo.fleet')}
        imageWidth={1600}
        imageHeight={1000}
      />
      <section className="bg-[#161411] text-stone-100">
        <Reveal className="mx-auto max-w-6xl px-5 py-16 sm:px-8 lg:py-24">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#FF8A00]">{t('landing.features.bookings.t')}</p>
          <h2 className="mt-3 max-w-xl text-3xl font-semibold tracking-tight text-white">{t('landing.show.book.title')}</h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-stone-300">{t('landing.show.book.body')}</p>
          <ul className="mt-10 grid gap-4 sm:grid-cols-3">
            {(['bookings', 'contracts', 'cash'] as const).map((key) => (
              <li key={key} className="wp-card rounded-2xl border border-white/10 bg-white/5 p-5">
                <h3 className="text-sm font-semibold text-white">{t(`landing.features.${key}.t`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-400">{t(`landing.features.${key}.d`)}</p>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>
      <section className="mx-auto grid max-w-6xl gap-12 px-5 py-16 sm:px-8 lg:grid-cols-2 lg:py-24">
        <article>
          <div className="wp-photo overflow-hidden rounded-2xl shadow-lg shadow-stone-900/10">
            <MarketingPhoto
              name="handover-keys"
              alt={t('landing.photo.keys')}
              width={1400}
              height={933}
              className="aspect-[3/2] w-full object-cover"
            />
          </div>
          <h2 className="mt-6 text-2xl font-semibold tracking-tight text-stone-950">{t('landing.show.sign.title')}</h2>
          <p className="mt-3 text-sm leading-relaxed text-stone-600">{t('landing.show.sign.body')}</p>
        </article>
        <article className="lg:pt-16">
          <div className="wp-photo overflow-hidden rounded-2xl shadow-lg shadow-stone-900/10">
            <MarketingPhoto
              name="vehicle-preparation"
              alt={t('landing.photo.prep')}
              width={1400}
              height={933}
              className="aspect-[3/2] w-full object-cover"
            />
          </div>
          <h2 className="mt-6 text-2xl font-semibold tracking-tight text-stone-950">{t('landing.show.prep.title')}</h2>
          <p className="mt-3 text-sm leading-relaxed text-stone-600">{t('landing.show.prep.body')}</p>
          <h3 className="mt-8 text-lg font-semibold tracking-tight text-stone-950">{t('landing.show.cash.title')}</h3>
          <p className="mt-3 text-sm leading-relaxed text-stone-600">{t('landing.show.cash.body')}</p>
        </article>
      </section>
    </>
  );
}

function CapabilityIndex() {
  const { t } = useLocale();
  return (
    <section className="border-t border-stone-200 bg-white">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 lg:py-20">
        <Reveal>
          <div className="grid items-center gap-10 lg:grid-cols-[0.72fr_1.28fr]">
            <div className="wp-photo overflow-hidden rounded-2xl shadow-lg shadow-stone-900/10">
              <MarketingPhoto
                name="agency-team"
                alt={t('landing.photo.team')}
                width={1200}
                height={1500}
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9a3412]">{t('landing.features.title')}</p>
              <h2 className="mt-3 max-w-md text-3xl font-semibold tracking-tight text-stone-950">{t('landing.show.team.title')}</h2>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-stone-600">{t('landing.show.team.body')}</p>
            </div>
          </div>
        </Reveal>
        <ul className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <li key={feature.key} className="wp-card rounded-2xl border border-stone-200 bg-[#f4f1eb] p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-[#9a3412] shadow-sm">
                  <Icon size={18} />
                </span>
                <h3 className="mt-4 text-sm font-semibold text-stone-950">{t(`landing.features.${feature.key}.t`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-600">{t(`landing.features.${feature.key}.d`)}</p>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useLocale();
  return (
    <section id="how" className="scroll-mt-32 border-t border-stone-200">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 lg:py-24">
        <h2 className="max-w-md text-3xl font-semibold tracking-tight text-stone-950">{t('landing.how.title')}</h2>
        <p className="mt-3 max-w-lg text-sm leading-relaxed text-stone-600">{t('landing.how.subtitle')}</p>
        <ol className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step, index) => (
            <li key={step} className="wp-card rounded-2xl border border-stone-200 bg-white p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FF8A00] text-sm font-semibold text-stone-950">
                0{index + 1}
              </span>
              <h3 className="mt-4 text-sm font-semibold text-stone-950">{t(`landing.how.${step}.t`)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-stone-600">{t(`landing.how.${step}.d`)}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Pricing() {
  const { t } = useLocale();
  return (
    <section id="pricing" className="scroll-mt-32 bg-[#161411] text-stone-100">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 lg:py-24">
        <h2 className="text-3xl font-semibold tracking-tight text-white">{t('landing.pricing.title')}</h2>
        <p className="mt-3 max-w-lg text-sm leading-relaxed text-stone-400">{t('landing.pricing.subtitle')}</p>
        <div className="mt-12 grid gap-4 lg:grid-cols-3">
          {SUBSCRIPTION_TIERS.map((tier) => {
            const highlighted = tier === 'growth';
            return (
              <article
                key={tier}
                className={`wp-card flex flex-col rounded-2xl p-6 ${
                  highlighted ? 'bg-white text-stone-950 shadow-2xl shadow-black/20' : 'border border-white/10 bg-white/5'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold">{planName(tier)}</h3>
                  {highlighted && (
                    <span className="rounded-full bg-[#FF8A00] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-stone-950">
                      {t('landing.pricing.popular')}
                    </span>
                  )}
                </div>
                <p className={`mt-1 text-sm ${highlighted ? 'text-stone-500' : 'text-stone-400'}`}>{t(`landing.plan.${tier}.d`)}</p>
                <p className="mt-6 flex items-baseline gap-1.5">
                  <span className="text-4xl font-semibold tracking-tight tabular-nums">{TIER_MONTHLY_MAD[tier]}</span>
                  <span className={`text-xs font-semibold ${highlighted ? 'text-stone-500' : 'text-stone-400'}`}>
                    MAD {t('landing.pricing.monthly')}
                  </span>
                </p>
                <ul className="mt-6 space-y-3">
                  {[0, 1, 2].map((index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <Check size={16} className={`mt-0.5 shrink-0 ${highlighted ? 'text-[#FF8A00]' : 'text-[#FF8A00]'}`} />
                      {t(`landing.plan.${tier}.f${index}`)}
                    </li>
                  ))}
                </ul>
                <a
                  href="#demo"
                  className={`mt-8 inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold transition ${focusRing} ${
                    highlighted ? 'bg-[#FF8A00] text-stone-950 hover:bg-[#ea7900]' : 'border border-white/15 text-white hover:border-white/40'
                  }`}
                >
                  {t('landing.pricing.cta')}
                </a>
              </article>
            );
          })}
        </div>
        <p className="mt-6 text-xs leading-relaxed text-stone-500">{t('landing.pricing.note')} {t('landing.pricing.after')}</p>
      </div>
    </section>
  );
}

function Faq() {
  const { t } = useLocale();
  const [open, setOpen] = useState<string | null>('1');
  return (
    <section id="faq" className="scroll-mt-32">
      <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8 lg:py-24">
        <h2 className="text-3xl font-semibold tracking-tight text-stone-950">{t('landing.faq.title')}</h2>
        <div className="mt-8 space-y-2">
          {['1', '2', '3', '4', '5'].map((index) => {
            const isOpen = open === index;
            const panelId = `faq-panel-${index}`;
            return (
              <div key={index} className={`rounded-2xl border bg-white ${isOpen ? 'border-[#fdba74]' : 'border-stone-200'}`}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpen(isOpen ? null : index)}
                  className={`flex w-full items-center justify-between gap-4 rounded-2xl px-4 py-4 text-start ${focusRing}`}
                >
                  <span className="text-sm font-semibold text-stone-950">{t(`landing.faq.${index}.q`)}</span>
                  <ChevronDown size={18} className={`shrink-0 text-stone-400 transition-transform ${isOpen ? 'rotate-180 text-[#9a3412]' : ''}`} />
                </button>
                {isOpen && (
                  <p id={panelId} className="px-4 pb-4 text-sm leading-relaxed text-stone-600">
                    {t(`landing.faq.${index}.a`)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const { t } = useLocale();
  return (
    <section id="demo" className="scroll-mt-32 border-t border-stone-200 bg-[#f6f3ee]">
      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-16 sm:px-8 lg:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)] lg:items-start lg:gap-12 lg:py-20">
        <div className="lg:sticky lg:top-32">
          <img
            src="/marketing/fleet-row.webp"
            alt=""
            width={1600}
            height={1000}
            loading="lazy"
            decoding="async"
            className="mb-6 h-40 w-full rounded-2xl object-cover"
          />
          <h2 className="text-3xl font-semibold tracking-tight text-stone-950">{t('landing.contact.title')}</h2>
          <p className="mt-3 text-sm leading-relaxed text-stone-600">{t('landing.contact.subtitle')}</p>
          <p className="mt-3 text-sm leading-relaxed text-stone-600">{t('landing.contact.next')}</p>
          <a href="mailto:contact@fleetava.ma" className={`mt-5 inline-flex items-center gap-2 text-sm font-semibold text-stone-950 ${focusRing}`}>
            <Mail size={16} className="text-[#FF8A00]" />
            contact@fleetava.ma
          </a>
        </div>
        <div className="demo-card rounded-3xl border border-stone-200 bg-white p-5 shadow-[0_16px_40px_rgba(22,20,17,0.06)] sm:p-8">
          <DemoRequestForm />
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useLocale();
  return (
    <footer className="bg-[#161411] text-stone-300">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:px-8 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <Brand light />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-stone-400">{t('landing.footer.tagline')}</p>
        </div>
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">{t('landing.footer.product')}</h2>
          <ul className="mt-4 space-y-2 text-sm">
            <li><a href="#features" className={`hover:text-white ${focusRing}`}>{t('landing.nav.features')}</a></li>
            <li><a href="#how" className={`hover:text-white ${focusRing}`}>{t('landing.nav.how')}</a></li>
            <li><a href="#pricing" className={`hover:text-white ${focusRing}`}>{t('landing.nav.pricing')}</a></li>
            <li><a href="#faq" className={`hover:text-white ${focusRing}`}>{t('landing.nav.faq')}</a></li>
          </ul>
        </div>
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">{t('landing.footer.account')}</h2>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/login?callbackUrl=/super-admin" className={`hover:text-white ${focusRing}`}>{t('landing.nav.login')}</Link></li>
            <li><a href="#demo" className={`hover:text-white ${focusRing}`}>{t('landing.nav.demo')}</a></li>
            <li>
              <a href="mailto:contact@fleetava.ma" className={`hover:text-white ${focusRing}`}>contact@fleetava.ma</a>
            </li>
          </ul>
        </div>
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">{t('landing.footer.photos')}</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {PHOTO_CREDITS.map((credit) => (
              <li key={credit.href}>
                <a href={credit.href} className={`hover:text-white ${focusRing}`} rel="noreferrer">
                  {credit.label}
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-stone-500">Pexels</p>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-stone-500">
        © {new Date().getFullYear()} fleetava. {t('landing.footer.rights')}
      </div>
    </footer>
  );
}

function LandingContent() {
  const { t, lang } = useLocale();

  useEffect(() => {
    const title = t('landing.meta.title');
    const description = t('landing.meta.description');
    document.title = title;
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    if (typeof window !== 'undefined') {
      setMeta('property', 'og:image', `${window.location.origin}/marketing/hero-suv.webp`);
    }
  }, [t, lang]);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'fleetava',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: t('landing.meta.description'),
  };

  return (
    <div className="theme-surface marketing-page min-h-screen bg-[#f4f1eb] text-stone-900">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <main>
        <Hero />
        <ProofStrip />
        <ProductStories />
        <CapabilityIndex />
        <HowItWorks />
        <Pricing />
        <Faq />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default function LandingPage() {
  return <LandingContent />;
}
